import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';

interface Payment {
  id: string;
  user_id: string;
  subscription_id?: string;
  plan_id?: string;
  amount: number;
  fee: number;
  net_amount?: number;
  status: string;
  payment_method: string;
  gateway: string;
  gateway_transaction_id?: string;
  billing_date?: string;
  paid_at?: string;
  created_at: string;
  webhook_received?: boolean;
  gateway_response?: string;
  notes?: string;
  attempts?: number;
}

const statusColor: Record<string, string> = {
  paid:       'bg-emerald-500/20 text-emerald-400',
  approved:   'bg-emerald-500/20 text-emerald-400',
  pending:    'bg-yellow-500/20 text-yellow-400',
  in_process: 'bg-yellow-500/20 text-yellow-400',
  failed:     'bg-red-500/20 text-red-400',
  rejected:   'bg-red-500/20 text-red-400',
  refunded:   'bg-violet-500/20 text-violet-400',
  cancelled:  'bg-white/10 text-white/40',
};
const statusLabel: Record<string, string> = {
  paid:       'Pago',
  approved:   'Aprovado',
  pending:    'Pendente',
  in_process: 'Em análise',
  failed:     'Recusado',
  rejected:   'Recusado',
  refunded:   'Reembolsado',
  cancelled:  'Cancelado',
};

const methodIcon: Record<string, string> = {
  credit_card: 'ri-bank-card-line',
  pix:         'ri-qr-code-line',
  boleto:      'ri-file-text-line',
  debit_card:  'ri-bank-card-2-line',
};

const PERIODS = [
  { label: 'Todos', value: 'all' },
  { label: 'Hoje',    value: '1d'  },
  { label: '7 dias',  value: '7d'  },
  { label: '30 dias', value: '30d' },
  { label: '90 dias', value: '90d' },
];

const AdminPagamentosPage = () => {
  const [payments, setPayments]     = useState<Payment[]>([]);
  const [profileMap, setProfileMap] = useState<Record<string, string>>({});
  const [planMap, setPlanMap]       = useState<Record<string, { name: string }>>({});
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [filterStatus, setFilterStatus]   = useState('all');
  const [filterGateway, setFilterGateway] = useState('all');
  const [filterPeriod, setFilterPeriod]   = useState('all');
  const [filterPlan, setFilterPlan]       = useState('all');
  const [search, setSearch]         = useState('');
  const [detail, setDetail]         = useState<Payment | null>(null);
  const [editNotes, setEditNotes]   = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [plans, setPlans]           = useState<{ id: string; name: string }[]>([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    // ── 1. Fetch payments directly (no PostgREST join — avoids FK dependency) ──
    const { data: paymentsData, error: paymentsError } = await supabase
      .from('payments')
      .select('id, user_id, subscription_id, plan_id, amount, fee, net_amount, status, payment_method, gateway, gateway_transaction_id, billing_date, paid_at, created_at, webhook_received, gateway_response, notes, attempts')
      .order('created_at', { ascending: false });

    if (paymentsError) {
      console.error('[AdminPagamentos] payments fetch error:', paymentsError);
      setError(`Erro ao carregar pagamentos: ${paymentsError.message} (code: ${paymentsError.code})`);
      setLoading(false);
      return;
    }

    const rawPayments = (paymentsData ?? []) as Payment[];
    setPayments(rawPayments);

    // ── 2. Fetch plans ──────────────────────────────────────────────────────────
    const { data: plansData } = await supabase
      .from('plans')
      .select('id, name')
      .order('name');

    const plansArr = (plansData ?? []) as { id: string; name: string }[];
    setPlans(plansArr);
    const pm: Record<string, { name: string }> = {};
    plansArr.forEach((p) => { pm[p.id] = { name: p.name }; });
    setPlanMap(pm);

    // ── 3. Fetch profiles for user_ids found in payments ────────────────────────
    const userIds = Array.from(new Set(rawPayments.map((p) => p.user_id).filter(Boolean)));
    if (userIds.length > 0) {
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);

      if (profilesError) {
        console.error('[AdminPagamentos] profiles fetch error:', profilesError);
      } else {
        const prfMap: Record<string, string> = {};
        (profilesData ?? []).forEach((pr: { id: string; full_name: string }) => {
          prfMap[pr.id] = pr.full_name;
        });
        setProfileMap(prfMap);
      }
    }

    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmt         = (d?: string) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';
  const fmtDT       = (d?: string) => d ? new Date(d).toLocaleString('pt-BR') : '—';
  const fmtCurrency = (v?: number) => v != null ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';

  const parseGatewayResponse = (raw?: string) => {
    try { return raw ? JSON.parse(raw) : null; } catch { return null; }
  };

  const getFailureReason = (p: Payment) => {
    const gw = parseGatewayResponse(p.gateway_response);
    return gw?.failure_reason || gw?.mp_status_detail || null;
  };

  const isInPeriod = (p: Payment) => {
    if (filterPeriod === 'all') return true;
    const days = filterPeriod === '1d' ? 1 : filterPeriod === '7d' ? 7 : filterPeriod === '30d' ? 30 : 90;
    const since = new Date();
    since.setDate(since.getDate() - days);
    return new Date(p.created_at) >= since;
  };

  const filtered = payments.filter((p) => {
    if (filterStatus !== 'all') {
      // Normalize for comparison: rejected = failed, in_process = pending
      const normalize = (s: string) => (s === 'rejected' ? 'failed' : s === 'in_process' ? 'pending' : s);
      if (normalize(p.status) !== normalize(filterStatus)) return false;
    }
    if (filterGateway !== 'all' && p.gateway !== filterGateway) return false;
    if (filterPlan !== 'all' && p.plan_id !== filterPlan) return false;
    if (!isInPeriod(p)) return false;
    if (search) {
      const name = (profileMap[p.user_id] || '').toLowerCase();
      if (!name.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  const paid = payments.filter((p) => p.status === 'paid' || p.status === 'approved');
  const now = new Date();
  const paidThisMonth = paid.filter((p) => {
    const d = new Date(p.paid_at || p.created_at);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const totalRevenue   = paid.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
  const monthRevenue   = paidThisMonth.reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
  const pendingCount   = payments.filter((p) => p.status === 'pending').length;
  const failedCount    = payments.filter((p) => p.status === 'failed').length;
  const refundedTotal  = payments.filter((p) => p.status === 'refunded').reduce((acc, p) => acc + (Number(p.amount) || 0), 0);
  const avgTicket      = paid.length > 0 ? totalRevenue / paid.length : 0;

  const gateways = Array.from(new Set(payments.map((p) => p.gateway).filter(Boolean)));

  const openDetail = (p: Payment) => { setDetail(p); setEditNotes(p.notes || ''); };

  const saveNotes = async () => {
    if (!detail) return;
    setSavingNotes(true);
    await supabase.from('payments').update({ notes: editNotes }).eq('id', detail.id);
    setPayments((prev) => prev.map((p) => p.id === detail.id ? { ...p, notes: editNotes } : p));
    setDetail({ ...detail, notes: editNotes });
    setSavingNotes(false);
  };

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="text-[24px] font-700 text-white">Gestão de Pagamentos</h1>
          <p className="text-white/40 text-[14px] mt-1">Central completa de pagamentos da plataforma</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white/60 text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line" />Atualizar
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 mb-5 flex items-start gap-3">
          <i className="ri-error-warning-line text-red-400 text-lg flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[13px] font-700 text-red-300 mb-1">Erro ao carregar pagamentos</p>
            <p className="text-[12px] text-red-400/70">{error}</p>
            <button onClick={fetchData} className="mt-2 text-[12px] text-red-300 underline cursor-pointer">Tentar novamente</button>
          </div>
        </div>
      )}

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Receita Total',  value: fmtCurrency(totalRevenue), icon: 'ri-money-dollar-circle-line', color: 'bg-emerald-600', sub: `${paid.length} aprovados` },
          { label: 'Receita do Mês', value: fmtCurrency(monthRevenue), icon: 'ri-calendar-check-line',      color: 'bg-violet-600', sub: `${paidThisMonth.length} pagamentos` },
          { label: 'Total de Transações', value: String(payments.length), icon: 'ri-bar-chart-line', color: 'bg-sky-700', sub: 'Todos os status' },
          { label: 'Ticket Médio',   value: fmtCurrency(avgTicket),   icon: 'ri-price-tag-3-line',           color: 'bg-pink-700',   sub: 'Por transação aprovada' },
        ].map((s) => (
          <div key={s.label} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-4">
            <div className={`w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0 ${s.color}`}>
              <i className={`${s.icon} text-white text-[20px]`} />
            </div>
            <div>
              <p className="text-[11px] text-white/40 mb-0.5">{s.label}</p>
              <p className="text-[16px] font-700 text-white">{s.value}</p>
              <p className="text-[11px] text-white/30">{s.sub}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Pendentes',   value: pendingCount,            icon: 'ri-time-line',         color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20' },
          { label: 'Recusados',   value: failedCount,             icon: 'ri-close-circle-line', color: 'text-red-400 bg-red-500/10 border-red-500/20' },
          { label: 'Reembolsos',  value: fmtCurrency(refundedTotal), icon: 'ri-refund-line',    color: 'text-violet-400 bg-violet-500/10 border-violet-500/20' },
        ].map((s) => (
          <div key={s.label} className={`border rounded-2xl p-4 flex items-center gap-3 ${s.color}`}>
            <i className={`${s.icon} text-[22px]`} />
            <div>
              <p className="text-[11px] text-white/40">{s.label}</p>
              <p className="text-[18px] font-700">{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-5 space-y-3">
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-[11px] text-white/30 uppercase tracking-wider font-600 mr-1">Status</span>
          {['all', 'approved', 'pending', 'failed', 'paid', 'refunded', 'cancelled'].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${filterStatus === s ? 'bg-violet-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
            >
              {s === 'all' ? 'Todos' : (statusLabel[s] ?? s)}
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-[11px] text-white/30 uppercase tracking-wider font-600 mr-1">Período</span>
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setFilterPeriod(p.value)}
              className={`px-3 py-1.5 rounded-xl text-[12px] font-600 transition-colors cursor-pointer whitespace-nowrap ${filterPeriod === p.value ? 'bg-violet-600 text-white' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex gap-3 flex-wrap items-center">
          <select
            value={filterGateway}
            onChange={(e) => setFilterGateway(e.target.value)}
            className="bg-white/5 border border-white/10 text-white/60 text-[12px] rounded-xl px-3 py-1.5 outline-none cursor-pointer"
          >
            <option value="all">Todos os gateways</option>
            {gateways.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
          <select
            value={filterPlan}
            onChange={(e) => setFilterPlan(e.target.value)}
            className="bg-white/5 border border-white/10 text-white/60 text-[12px] rounded-xl px-3 py-1.5 outline-none cursor-pointer"
          >
            <option value="all">Todos os planos</option>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <div className="relative ml-auto">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/30 text-[13px]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar usuário..."
              className="bg-white/5 border border-white/10 rounded-xl pl-8 pr-3 py-1.5 text-white text-[12px] outline-none focus:border-violet-500 placeholder:text-white/20 w-[180px]"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line text-2xl text-violet-400 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-white/30">
            <i className="ri-bank-card-line text-3xl mb-2 block" />
            <p className="text-[14px]">Nenhum pagamento encontrado</p>
            <p className="text-[12px] mt-1 text-white/20">
              {payments.length === 0 ? 'Ainda não há transações registradas no sistema.' : 'Nenhum resultado para os filtros aplicados.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px]">
              <thead>
                <tr className="border-b border-white/10">
                  {['Usuário', 'Plano', 'Valor', 'Taxa', 'Líquido', 'Status', 'Gateway', 'Método', 'Cobrança', 'Pagamento', 'ID Transação', 'Ações'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-[11px] font-600 text-white/30 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const failReason = getFailureReason(p);
                  const userName = profileMap[p.user_id] || p.user_id?.substring(0, 8) + '...' || '—';
                  const planName = p.plan_id ? (planMap[p.plan_id]?.name || '—') : '—';
                  return (
                    <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                      <td className="px-4 py-3.5 text-[13px] text-white font-500 max-w-[140px]">
                        <p className="truncate">{userName}</p>
                      </td>
                      <td className="px-4 py-3.5 text-[12px] text-white/50">{planName}</td>
                      <td className="px-4 py-3.5 text-[13px] text-white font-600">{fmtCurrency(Number(p.amount))}</td>
                      <td className="px-4 py-3.5 text-[12px] text-red-400">{p.fee ? fmtCurrency(p.fee) : '—'}</td>
                      <td className="px-4 py-3.5 text-[13px] text-emerald-400 font-500">
                        {fmtCurrency(p.net_amount ?? (Number(p.amount) - (p.fee || 0)))}
                      </td>
                      <td className="px-4 py-3.5">
                        <div className="space-y-1">
                          <span className={`text-[11px] font-600 px-2 py-0.5 rounded-full ${statusColor[p.status] || 'bg-white/10 text-white/40'}`}>
                            {statusLabel[p.status] || p.status}
                          </span>
                          {failReason && (
                            <p className="text-[10px] text-red-300/70 max-w-[140px] leading-tight">{failReason}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-[12px] text-white/50 capitalize">{p.gateway || '—'}</td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <i className={`${methodIcon[p.payment_method] || 'ri-money-dollar-circle-line'} text-white/30 text-[13px]`} />
                          <span className="text-[12px] text-white/50 capitalize">{p.payment_method?.replace(/_/g, ' ') || '—'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-[12px] text-white/40 whitespace-nowrap">{fmt(p.billing_date)}</td>
                      <td className="px-4 py-3.5 text-[12px] text-white/40 whitespace-nowrap">{fmt(p.paid_at)}</td>
                      <td className="px-4 py-3.5 text-[11px] text-white/30 font-mono whitespace-nowrap">
                        {p.gateway_transaction_id ? `...${p.gateway_transaction_id.slice(-10)}` : '—'}
                      </td>
                      <td className="px-4 py-3.5">
                        <button
                          onClick={() => openDetail(p)}
                          className="w-7 h-7 flex items-center justify-center bg-white/5 hover:bg-white/15 rounded-lg transition-colors cursor-pointer"
                        >
                          <i className="ri-eye-line text-white/50 text-[13px]" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-5 py-3 border-t border-white/5 flex items-center justify-between">
          <p className="text-[12px] text-white/30">
            {filtered.length} pagamento{filtered.length !== 1 ? 's' : ''} exibido{filtered.length !== 1 ? 's' : ''}
            {payments.length !== filtered.length && ` (de ${payments.length} total)`}
          </p>
          {payments.length > 0 && (
            <p className="text-[11px] text-white/20">
              Última transação: {fmt(payments[0]?.created_at)}
            </p>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#1a1a2e] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#1a1a2e]">
              <div>
                <h2 className="text-[16px] font-700 text-white">Detalhe do Pagamento</h2>
                <p className="text-[12px] text-white/30 font-mono mt-0.5">{detail.id}</p>
              </div>
              <button onClick={() => setDetail(null)} className="w-8 h-8 flex items-center justify-center bg-white/5 hover:bg-white/10 rounded-lg cursor-pointer transition-colors">
                <i className="ri-close-line text-white/60" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { label: 'Usuário',          value: profileMap[detail.user_id] || detail.user_id },
                  { label: 'Plano',            value: detail.plan_id ? (planMap[detail.plan_id]?.name || detail.plan_id) : '—' },
                  { label: 'Valor Bruto',      value: fmtCurrency(Number(detail.amount)) },
                  { label: 'Taxa',             value: fmtCurrency(detail.fee) },
                  { label: 'Valor Líquido',    value: fmtCurrency(detail.net_amount ?? (Number(detail.amount) - (detail.fee || 0))) },
                  { label: 'Status',           value: statusLabel[detail.status] || detail.status },
                  { label: 'Gateway',          value: detail.gateway || '—' },
                  { label: 'Método',           value: detail.payment_method?.replace(/_/g, ' ') || '—' },
                  { label: 'Data de Cobrança', value: fmtDT(detail.billing_date) },
                  { label: 'Data de Pagamento',value: fmtDT(detail.paid_at) },
                  { label: 'Tentativas',       value: String(detail.attempts ?? 1) },
                  { label: 'Webhook Recebido', value: detail.webhook_received ? 'Sim' : 'Não' },
                ].map((row) => (
                  <div key={row.label} className="bg-white/5 rounded-xl p-3">
                    <p className="text-[11px] text-white/30 mb-1">{row.label}</p>
                    <p className="text-[13px] text-white font-500 break-all">{row.value}</p>
                  </div>
                ))}
              </div>

              {/* Failure reason */}
              {(() => {
                const gw = parseGatewayResponse(detail.gateway_response);
                const reason = gw?.failure_reason || gw?.mp_status_detail;
                return (detail.status === 'failed' || gw?.mp_status === 'rejected') && reason ? (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3">
                    <p className="text-[11px] text-red-300/70 uppercase tracking-wider font-600 mb-1">Motivo da Recusa</p>
                    <p className="text-[13px] text-red-300 font-500">{reason}</p>
                    {gw?.mp_status_detail && gw.mp_status_detail !== reason && (
                      <p className="text-[11px] text-white/30 font-mono mt-1">{gw.mp_status_detail}</p>
                    )}
                  </div>
                ) : null;
              })()}

              {detail.gateway_transaction_id && (
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-[11px] text-white/30 mb-1">ID da Transação (Mercado Pago)</p>
                  <p className="text-[13px] text-white font-mono break-all">{detail.gateway_transaction_id}</p>
                </div>
              )}

              {detail.gateway_response && (
                <div>
                  <p className="text-[12px] text-white/40 font-600 uppercase tracking-wider mb-2">Retorno do Gateway</p>
                  <pre className="bg-black/30 border border-white/10 rounded-xl p-3 text-[11px] text-white/60 font-mono overflow-x-auto whitespace-pre-wrap break-all max-h-[200px] overflow-y-auto">
                    {(() => { try { return JSON.stringify(JSON.parse(detail.gateway_response!), null, 2); } catch { return detail.gateway_response; } })()}
                  </pre>
                </div>
              )}

              <div>
                <p className="text-[12px] text-white/40 font-600 uppercase tracking-wider mb-2">Observações Administrativas</p>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white text-[13px] outline-none focus:border-violet-500 placeholder:text-white/20 resize-none"
                  placeholder="Adicionar observação interna..."
                />
                <button
                  onClick={saveNotes}
                  disabled={savingNotes}
                  className="mt-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white text-[12px] font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  {savingNotes ? 'Salvando...' : 'Salvar Observação'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPagamentosPage;
